import { GetJWTHandlerPayload } from '@hqo/hqo-miniapp-client-sdk';
import { getMiniappSdkClient } from 'components/miniapp-sdk-provider/miniapp-sdk.hook';
import { Dispatch, MiddlewareAPI } from 'redux';
import { PROMISE_TIMEOUT } from 'shared/consts';
import { NOT_FOUND_ERROR_CODE, UNAUTHORIZED_ERROR_CODE } from 'shared/consts/error-codes';
import { RootAction } from 'store/actions';
import { getAppInstanceConfigs } from 'store/app-instance-configs/actions';
import { getBuilding } from 'store/building/actions';
import { saveExternalConfig } from 'store/config/actions';
import { showGenericErrorNotification } from 'store/errors/actions';

import { RootState } from 'store/reducer';

import { getBuildingTheme } from 'store/theme/actions';
import { getUiMetadata } from 'store/ui-metadata/actions';
import { getCurrentUser, refreshToken } from 'store/user/actions';
import { isServerUnreachable } from 'store/user/network-failure';
import { getResourceTimeRanges } from 'store/resource-time-ranges/actions';

import { isActionOf } from 'typesafe-actions';

const failedActionsTypes = [
  getAppInstanceConfigs.failure,
  getBuildingTheme.failure,
  getCurrentUser.failure,
  getBuilding.failure,
  getUiMetadata.failure,
  getResourceTimeRanges.failure,
];

const failedActionsTypesWithErrorScreenRedirect = failedActionsTypes;

export const tokenExpiryMiddleware =
  (store: MiddlewareAPI<Dispatch<RootAction>, RootState>) =>
  (next: Dispatch<RootAction>) =>
  async (action: RootAction) => {
    const isAsyncActionTypeFailed = isActionOf(failedActionsTypes)(action);
    if (
      isAsyncActionTypeFailed &&
      'status' in action.payload.error &&
      (action.payload.error.status === UNAUTHORIZED_ERROR_CODE || action.payload.error.status === NOT_FOUND_ERROR_CODE)
    ) {
      const miniappSdkClient = getMiniappSdkClient(process.env.REACT_APP_UUID);

      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          store.dispatch(
            showGenericErrorNotification({
              action: action.type,
              error: action.payload.error,
              errorCode: action.payload.errorCode,
            }),
          );
          reject();
        }, PROMISE_TIMEOUT);

        miniappSdkClient.identity.getJWT();

        miniappSdkClient.on('getJWTResponse', (data: GetJWTHandlerPayload) => {
          clearTimeout(timeout);
          resolve();

          const authToken = data.token.includes('Bearer') ? data.token : `Bearer ${data.token}`;

          store.dispatch(saveExternalConfig({ authToken }));
        });
      });

      store.dispatch(refreshToken());
    } else if (isActionOf(getCurrentUser.failure)(action) && isServerUnreachable(action.payload.error)) {
      // Server unreachable: let the reducer end the loading state (the branch below never calls `next`).
      next(action);
    } else if (isActionOf(failedActionsTypesWithErrorScreenRedirect)(action)) {
      store.dispatch(
        showGenericErrorNotification({
          action: action.type,
          error: action.payload.error,
          errorCode: action.payload.errorCode,
        }),
      );
    } else {
      next(action);
    }
  };
